import { memo, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import {
  trackCompareGridEvents,
  CompareGridLocations,
  CompareGridEventNames,
} from '@utils/gtm/ga4/trackCompareGridEvents';
import { useTranslation } from 'next-i18next';
import { useLocale } from '@hooks/useLocale';
import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react';
import IconButton from '_components/UI/IconButton';
import LinkButton from '_components/UI/LinkButton';
import DOMPurify from 'isomorphic-dompurify';
import { MecProduct } from '@adapters/bigcommerce/types/product';
import useProduct from '@adapters/bigcommerce/hooks/product/useProduct';
import { formatList } from '@utils/comparison/getCompareAttribute';
import {
  FranchiseAttributeType,
  getFranchiseAttributeType,
  getCompareSpecArray,
} from '@utils/comparison/franchise/getFranchiseCompareAttribute';
import { slugify } from '@utils/url';
import { getCtaAriaLabel } from '@utils/comparison/getCtaAriaLabel';

import CompareHeading, { hasHeading } from '../CompareHeading';
import FranchiseColumn from './columns/FranchiseColumn';
import LoadingSkeleton from './LoadingSkeleton';
import {
  CompareItem,
  CompareLocale,
  FranchiseCompareAttribute,
  FranchiseCompareTableProps,
} from './types';

import styles from './FranchiseCompareTable.module.scss';

const NAME_ATTR: FranchiseCompareAttribute = {
  key: 'name',
  label: { en: 'Name', fr: 'Nom' },
};

const LOCATION = CompareGridLocations.FranchiseCompare;

// An attribute is identified by its code or either of its labels, since merch
// can name a spec however they like in Akeneo.
const getAttributeTypes = (
  attr: FranchiseCompareAttribute
): FranchiseAttributeType[] =>
  [attr.key, attr.label?.en, attr.label?.fr].map((key) =>
    getFranchiseAttributeType(key ?? '')
  );

const getLocalized = (
  text: Partial<Record<CompareLocale, string>> | undefined,
  locale: CompareLocale
): string => text?.[locale]?.trim() ?? '';

const FranchiseCompareTable = ({
  surface,
  seedCode,
  config,
}: FranchiseCompareTableProps) => {
  const { asPath } = useRouter();
  const path = asPath.split('?')[0];
  const { t } = useTranslation('comparison');
  const activeLocale = useLocale();
  const locale: CompareLocale = activeLocale === 'fr' ? 'fr' : 'en';
  const { getProduct } = useProduct();
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);
  const hasTrackedScroll = useRef(false);

  const [bcProductsByCode, setBcProductsByCode] = useState<
    Map<string, MecProduct>
  >(new Map());
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const items = useMemo(() => config.items ?? [], [config.items]);
  const { contextLink, heading, productTypeLabel } = config;
  const contextLinkTarget =
    contextLink?.openInNewTab === false ? undefined : '_blank';

  const itemCodes = useMemo(
    () => items.map((item) => item?.id).filter((id): id is string => !!id),
    [items]
  );

  const seedIndex = seedCode
    ? items.findIndex((item) => item?.id === seedCode)
    : -1;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const handleScroll = () => {
      updateScrollState();
      if (!hasTrackedScroll.current) {
        hasTrackedScroll.current = true;
        trackCompareGridEvents({
          eventName: CompareGridEventNames.ScrollCompareTable,
          interactionLocation: LOCATION,
          path,
        });
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      ro.disconnect();
    };
  }, [updateScrollState, isLoadingProducts, items.length, path]);

  const handleSeedNameClick = useCallback(
    (
      e: React.MouseEvent<HTMLAnchorElement>,
      linkName: string,
      href: string,
      index: number
    ) => {
      const atc = document.getElementById('add-to-cart');
      if (!atc) return;
      trackCompareGridEvents({
        eventName: CompareGridEventNames.ClickCompareTable,
        interactionLocation: LOCATION,
        linkUrl: href,
        linkText: linkName,
        index,
        path,
      });
      e.preventDefault();
      atc.scrollIntoView({ behavior: 'smooth', block: 'end' });
    },
    [path]
  );

  const scrollByColumn = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    const table = tableRef.current;
    if (!el) return;
    const firstProductHeader = table?.querySelector<HTMLElement>(
      'thead tr th:nth-child(2)'
    );
    const colWidth = firstProductHeader?.offsetWidth ?? 304;
    el.scrollBy({
      left: direction === 'left' ? -colWidth : colWidth,
      behavior: 'smooth',
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableElement>) => {
      const table = tableRef.current;
      if (!table) return;

      const focusable = Array.from(
        table.querySelectorAll<HTMLElement>(
          'td[tabindex="0"], th[tabindex="0"], a, button, [tabindex="0"]'
        )
      );
      const idx = focusable.findIndex((el) => el === document.activeElement);
      if (idx === -1) return;

      let next = idx;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          next = Math.min(idx + 1, focusable.length - 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          next = Math.max(idx - 1, 0);
          break;
        case 'ArrowDown':
        case 'ArrowUp': {
          e.preventDefault();
          const cell = focusable[idx].closest<HTMLElement>('td, th');
          if (!cell) return;
          const row = cell?.closest('tr');
          if (!cell || !row) return;
          const allRows = Array.from(
            table.querySelectorAll('tbody tr, thead tr')
          );
          const rowIdx = allRows.indexOf(row);
          const delta = e.key === 'ArrowDown' ? 1 : -1;
          const nextRow = allRows[rowIdx + delta];
          if (nextRow) {
            const colIdx = Array.from(row.children).indexOf(cell);
            const nextCell = nextRow.children[colIdx] as HTMLElement;
            if (nextCell) {
              const focusableInCell =
                nextCell.querySelector<HTMLElement>(
                  'a, button, [tabindex="0"]'
                ) ?? (nextCell.hasAttribute('tabindex') ? nextCell : null);
              focusableInCell?.focus();
            }
          }
          return;
        }
        case 'Home':
          e.preventDefault();
          next = 0;
          break;
        case 'End':
          e.preventDefault();
          next = focusable.length - 1;
          break;
        default:
          return;
      }

      if (next !== idx) focusable[next]?.focus();
    },
    []
  );

  const handleTableClick = ({
    linkName,
    href,
    index,
  }: {
    linkName: string;
    href: string;
    index: number | null;
  }) => {
    trackCompareGridEvents({
      eventName: CompareGridEventNames.ClickCompareTable,
      interactionLocation: LOCATION,
      linkUrl: href,
      linkText: linkName,
      index,
      path,
    });
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;
          trackCompareGridEvents({
            eventName: CompareGridEventNames.ViewCompareTable,
            interactionLocation: LOCATION,
            path,
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // isLoadingProducts in deps ensures the effect re-runs once the container
    // div replaces the loading skeleton (containerRef.current is null before)
  }, [isLoadingProducts, path]);

  useEffect(() => {
    let cancelled = false;
    if (itemCodes.length === 0) {
      setBcProductsByCode(new Map());
      setIsLoadingProducts(false);
      return undefined;
    }
    setIsLoadingProducts(true);
    const fetchProducts = async () => {
      const fetched = await Promise.all(
        itemCodes.map((code) => getProduct(code))
      );
      if (cancelled) return;
      const next = new Map<string, MecProduct>();
      fetched.forEach((fetchedProduct, i) => {
        if (fetchedProduct) next.set(itemCodes[i], fetchedProduct);
      });
      setBcProductsByCode(next);
      setIsLoadingProducts(false);
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [getProduct, itemCodes]);

  // Ideal-for values render under the product name, so the attribute never
  // gets a row of its own.
  const idealForAttr = useMemo(
    () =>
      (config.attributes ?? []).find((attr) =>
        getAttributeTypes(attr).includes('idealFor')
      ),
    [config.attributes]
  );

  // Franchise tables compare a series rather than buyable variants, so price
  // is never part of the comparison.
  const sortedAttributes = useMemo(() => {
    const rest = (config.attributes ?? []).filter((attr) => {
      const types = getAttributeTypes(attr);
      return (
        !types.includes('name') &&
        !types.includes('price') &&
        !types.includes('idealFor') &&
        !types.includes('cta')
      );
    });
    return [NAME_ATTR, ...rest];
  }, [config.attributes]);

  // Franchise tables are authored per series and routinely ship items with an
  // empty title; the catalog name of the product the column resolves to is the
  // same string merch would have typed.
  const getItemTitle = useCallback(
    (item: CompareItem): string =>
      getLocalized(item.title, locale) ||
      bcProductsByCode.get(item.id)?.name ||
      '',
    [bcProductsByCode, locale]
  );

  const buildItemHref = useCallback(
    (item: CompareItem, linksToBuyZone: boolean): string => {
      if (linksToBuyZone) return '#add-to-cart';
      const ctaUrl = getLocalized(item.cta?.url, locale);
      if (ctaUrl) return ctaUrl;
      const slugSource =
        bcProductsByCode.get(item.id)?.slug || getLocalized(item.title, locale);
      const basePath = `/${locale}/product/${item.id}`;
      return slugSource ? `${basePath}/${slugify(slugSource)}` : basePath;
    },
    [bcProductsByCode, locale]
  );

  // On the PDP the seed's own page is already open, so its column scrolls to
  // the buy zone -- unless merch authored a link for it, which the seed follows
  // like every other column.
  const linksToBuyZone = useCallback(
    (item: CompareItem, index: number): boolean =>
      surface === 'pdp' &&
      index === seedIndex &&
      !getLocalized(item.cta?.url, locale),
    [surface, seedIndex, locale]
  );

  const getItemCta = useCallback(
    (item: CompareItem): { label: string; url: string } | null => {
      const label = getLocalized(item.cta?.label, locale);
      const url = getLocalized(item.cta?.url, locale);
      return label && url ? { label, url } : null;
    },
    [locale]
  );

  // The table closes with a row of per-item CTAs. The label column stays
  // empty: the links are self-describing, so a row header would add noise.
  const showCtaRow = useMemo(
    () => items.some((item) => !!getItemCta(item)),
    [items, getItemCta]
  );

  if (items.length === 0) return null;

  if (isLoadingProducts) {
    return <LoadingSkeleton />;
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${
        surface === 'category' ? styles.containerInSlice : ''
      }`}
    >
      <nav className={styles.navigation} aria-label={t('compare.nav.label')}>
        <IconButton
          icon={ArrowLeftIcon}
          type={canScrollLeft ? 'primary' : 'outline'}
          size="md"
          iconSize={24}
          iconWeight="light"
          shape="circular"
          disabled={!canScrollLeft}
          aria-label={t('compare.nav.previous')}
          onClick={() => scrollByColumn('left')}
        />
        <IconButton
          icon={ArrowRightIcon}
          type={canScrollRight ? 'primary' : 'outline'}
          size="md"
          iconSize={24}
          iconWeight="light"
          shape="circular"
          disabled={!canScrollRight}
          aria-label={t('compare.nav.next')}
          onClick={() => scrollByColumn('right')}
        />
      </nav>

      {hasHeading(heading) && (
        <CompareHeading heading={heading} className={styles.headingMobile} />
      )}

      <div ref={scrollRef} className={styles.scrollWrapper}>
        <table
          ref={tableRef}
          className={styles.table}
          aria-label={t('title')}
          role="grid"
          onKeyDown={handleKeyDown}
        >
          <caption className={styles.srOnly}>{t('title')}</caption>
          <colgroup>
            <col className={styles.labelCol} />
            {items.map((item) => (
              <col key={item.id} className={styles.productCol} />
            ))}
          </colgroup>

          <thead>
            <tr>
              <th
                scope="col"
                className={`${styles.labelCell} ${styles.labelHeaderCell}`}
              >
                {hasHeading(heading) ? (
                  <CompareHeading
                    heading={heading}
                    className={styles.heading}
                  />
                ) : (
                  <span className={styles.srOnly}>
                    {t('compare.attributes')}
                  </span>
                )}
                {contextLink && (
                  <LinkButton
                    variant="black"
                    href={contextLink.url}
                    target={contextLinkTarget}
                    onClick={() =>
                      handleTableClick({
                        linkName: contextLink.label,
                        href: contextLink.url,
                        index: null,
                      })
                    }
                  >
                    {contextLink.label}
                  </LinkButton>
                )}
              </th>
              {items.map((item, i) => {
                const isSeedLink = linksToBuyZone(item, i);
                const title = getItemTitle(item);
                const productHref = buildItemHref(item, isSeedLink);
                return (
                  <th
                    key={item.id}
                    scope="col"
                    className={styles.productHeaderCell}
                  >
                    <FranchiseColumn
                      name={title}
                      product={bcProductsByCode.get(item.id)}
                      imageUrl={item.image}
                      href={productHref}
                      onClick={
                        isSeedLink
                          ? (e) => handleSeedNameClick(e, title, productHref, i)
                          : () =>
                              handleTableClick({
                                linkName: title,
                                href: productHref,
                                index: i,
                              })
                      }
                      ariaLabel={title}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {sortedAttributes.map((attr, attrIdx) => {
              const type = getFranchiseAttributeType(attr.key);
              const isLastAttr =
                attrIdx === sortedAttributes.length - 1 && !showCtaRow;
              const isNameRow = type === 'name';
              const attrLabel =
                (isNameRow && productTypeLabel) ||
                (attr.label?.[locale] ?? attr.key);

              return (
                <tr
                  key={attr.key}
                  className={`${styles.attrRow} ${
                    isLastAttr ? styles.attrRowLast : ''
                  }`}
                >
                  <th scope="row" className={styles.labelCell}>
                    <span
                      className={
                        isNameRow && !productTypeLabel
                          ? styles.srOnly
                          : undefined
                      }
                    >
                      {attrLabel}
                    </span>
                  </th>
                  {items.map((item, i) => {
                    const isSeedLink = linksToBuyZone(item, i);
                    let content: React.ReactNode;

                    if (type === 'name') {
                      const idealForConjunction = t('compare.ideal.for.and');
                      const idealForLabel = t('compare.ideal.for');
                      const rawSubtitle = item?.subtitle?.[locale];
                      const subtitleItems = (
                        Array.isArray(rawSubtitle)
                          ? rawSubtitle
                          : [rawSubtitle ?? '']
                      ).filter(Boolean);
                      const idealForItems =
                        (idealForAttr &&
                          getCompareSpecArray(
                            item,
                            idealForAttr.key,
                            locale
                          )) ||
                        subtitleItems;
                      const idealFor = idealForItems.length
                        ? formatList(idealForItems, idealForConjunction)
                        : null;
                      const hasIdealForLabel = !!idealFor
                        ?.toLocaleLowerCase(locale)
                        .startsWith(idealForLabel.toLocaleLowerCase(locale));
                      const title = getItemTitle(item);
                      const nameHref = buildItemHref(item, isSeedLink);
                      content = (
                        <div className={styles.nameWrapper}>
                          <a
                            href={nameHref}
                            className={styles.productNameLink}
                            onClick={
                              isSeedLink
                                ? (e) =>
                                    handleSeedNameClick(e, title, nameHref, i)
                                : () =>
                                    handleTableClick({
                                      linkName: title,
                                      href: nameHref,
                                      index: i,
                                    })
                            }
                          >
                            <span className={styles.productName}>{title}</span>
                          </a>
                          {idealFor && (
                            <span className={styles.idealFor}>
                              {hasIdealForLabel
                                ? idealFor
                                : `${idealForLabel} ${idealFor.toLocaleLowerCase(
                                    locale
                                  )}`}
                            </span>
                          )}
                        </div>
                      );
                    } else {
                      const values = getCompareSpecArray(
                        item,
                        attr.key,
                        locale
                      );
                      if (values && values.length > 1) {
                        content = (
                          <ul className={styles.attrList}>
                            {values.map((value) => (
                              <li
                                key={value}
                                className={styles.attrValue}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(value),
                                }}
                              />
                            ))}
                          </ul>
                        );
                      } else {
                        content = values ? (
                          <span
                            className={styles.attrValue}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(values[0]),
                            }}
                          />
                        ) : (
                          <span className={styles.placeholder}>
                            {t('compare.placeholder')}
                          </span>
                        );
                      }
                    }

                    return (
                      <td
                        key={`${item.id}-${attr.key}`}
                        className={styles.dataCell}
                        tabIndex={0}
                        role="gridcell"
                      >
                        {type !== 'name' && (
                          <span className={styles.attrLabelMobile}>
                            {attrLabel}
                          </span>
                        )}
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {showCtaRow && (
              <tr className={`${styles.attrRow} ${styles.attrRowLast}`}>
                <td className={styles.labelCell} />
                {items.map((item, i) => {
                  const cta = getItemCta(item);
                  return (
                    <td
                      key={`${item.id}-cta`}
                      className={styles.dataCell}
                      role="gridcell"
                    >
                      {cta && (
                        <LinkButton
                          variant="black"
                          href={cta.url}
                          aria-label={getCtaAriaLabel(
                            cta.label,
                            getItemTitle(item),
                            (values) => t('compare.cta.aria.label', values)
                          )}
                          onClick={() =>
                            handleTableClick({
                              linkName: cta.label,
                              href: cta.url,
                              index: i,
                            })
                          }
                        >
                          {cta.label}
                        </LinkButton>
                      )}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {contextLink && (
        <div className={styles.contextLinkMobile}>
          <LinkButton
            variant="black"
            href={contextLink.url}
            target={contextLinkTarget}
            onClick={() =>
              handleTableClick({
                linkName: contextLink.label,
                href: contextLink.url,
                index: null,
              })
            }
          >
            {contextLink.label}
          </LinkButton>
        </div>
      )}
    </div>
  );
};

export default memo(FranchiseCompareTable);
